import { Check, Circle, CircleDashed, CircleDot, FileText } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  formatStepperFormStatusTooltip,
  stepperStatusDisplay,
  type StepperFormStatus,
  type StepperFormStatusInfo,
} from './stepperFormStatus';

const STATUS_COLOR: Record<StepperFormStatus, string> = {
  empty: 'tw:text-muted-foreground',
  'incomplete-draft': 'tw:text-amber-400',
  'complete-draft': 'tw:text-amber-300',
  'partially-signed': 'tw:text-sky-400',
  signed: 'tw:text-emerald-400',
};

type PlanningFormStatusDotProps = {
  info?: StepperFormStatusInfo;
  widgetName: string;
  formActive?: boolean;
};

const StatusIcon = ({
  status,
  formActive,
}: {
  status: StepperFormStatus;
  formActive?: boolean;
}) => {
  const className = cn(
    'tw:h-3 tw:w-3 tw:shrink-0',
    STATUS_COLOR[status],
    formActive && status === 'empty' ? 'tw:text-primary' : null
  );
  switch (status) {
    case 'incomplete-draft':
      return <CircleDashed className={className} aria-hidden />;
    case 'complete-draft':
      return <FileText className={className} aria-hidden />;
    case 'partially-signed':
      return <CircleDot className={className} aria-hidden />;
    case 'signed':
      return <Check className={className} aria-hidden />;
    default:
      return <Circle className={className} aria-hidden />;
  }
};

export function PlanningFormStatusDot({
  info,
  widgetName,
  formActive = false,
}: PlanningFormStatusDotProps) {
  const display = stepperStatusDisplay(info);
  const tooltip = formatStepperFormStatusTooltip(info);
  const colorClass = STATUS_COLOR[display.status];

  const marker =
    display.type === 'count' ? (
      <span
        className={cn(
          'tw:min-w-[1.35rem] tw:text-center tw:text-[9px] tw:font-semibold tw:tabular-nums tw:leading-none',
          colorClass,
          formActive && display.status === 'empty' ? 'tw:text-primary' : null
        )}
        data-testid={`planning-form-status-${widgetName}`}
        data-status={display.status}
        data-count={display.text}
      >
        {display.text}
      </span>
    ) : (
      <span
        data-testid={`planning-form-status-${widgetName}`}
        data-status={display.status}
      >
        <StatusIcon status={display.status} formActive={formActive} />
      </span>
    );

  if (!tooltip) return marker;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className="tw:inline-flex tw:shrink-0 tw:items-center">
            {marker}
          </span>
        }
      />
      <TooltipContent side="right" className="tw:max-w-[16rem] tw:text-[11px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
